import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Account,
  ChangeLog,
  DocumentComment,
  Pagination,
  Response,
} from '@ui/src';
import { GeneralInfo, Lock } from '../../types';
import {
  CustomerProps,
  TotalRiskLevelValues,
  FeeItem,
  Balance,
  TopUpVisibility,
  LinkedClient,
  Questionnarie,
} from '../../../customers/types';
import { individualCustomersApi } from '../services/individual';
import { customersComponentApi } from '../../../customers/store/services/customers';

const totalRiskLevelValuesMock: TotalRiskLevelValues = {
  good: [0, 24],
  medium: [25, 49],
};

export interface IndividualCustomerGeneralInfoState {
  isLoading?: boolean;
  baseInformation?: Partial<GeneralInfo>;
  baseInformationIsLoading?: boolean;
  customerId?: string;
  chatMessagesCount?: number;
  totalRiskLevels?: TotalRiskLevelValues;
  createAccountInProgress?: boolean;
  createAccountSuccess?: boolean;
  balances?: Balance[];
  topUpIsLoading: boolean;
  totalBalance?: {
    balance: number;
    currency: string[];
  };
  topUpOptions?: {
    companyName: string;
    id: string;
    visibleToClients: boolean;
  }[];
  topUpOptionsPagination?: Pagination;
  convertedToIndividual?: boolean;
  mainChat?: {
    id: string;
  };
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  activeLock?: Lock;
  topUpVisibility?: TopUpVisibility[];
  topUpIds?: string[];
  customerComments?: DocumentComment[];
  customerCommentsPagination?: Pagination;
}

const initialState: IndividualCustomerGeneralInfoState = {
  isLoading: true,
  topUpIsLoading: true,
  baseInformation: undefined,
  customerId: undefined,
  chatMessagesCount: undefined,
  totalRiskLevels: totalRiskLevelValuesMock,
  topUpIds: [],
};

export const individualCustomerGeneralInfoSlice = createSlice({
  name: 'individualCustomerInfo',
  initialState,
  reducers: {
    setBalances: (
      state,
      action: PayloadAction<{
        balances: Balance[];
        totalBCBalances?: number[];
      }>,
    ) => {
      state.balances = action.payload.balances;

      let balance = 0;

      const currencies: string[] = [];

      action.payload.balances.forEach((item) => {
        if (!currencies.find((currency) => currency === item.currencyId)) {
          currencies.push(item.currencyId);
        }
      });

      action?.payload?.totalBCBalances?.forEach((item) => {
        balance += item;
      });

      state.totalBalance = {
        balance,
        currency: currencies,
      };
    },
    setTopUpIds: (state, action: PayloadAction<string[]>) => {
      state.topUpIds = action.payload;
    },
    setConvertedToIndividual: (state, action: PayloadAction<boolean>) => {
      state.convertedToIndividual = action.payload;
    },
    clearGeneralState: (state) => {
      state.isLoading = true;
      state.balances = undefined;
      state.totalBalance = undefined;
      state.baseInformation = undefined;
      state.topUpOptions = undefined;
      state.customerId = undefined;
      state.topUpIsLoading = true;
      state.mainChat = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.activeLock = undefined;
      state.topUpVisibility = undefined;
      state.topUpIds = [];
      state.customerComments = undefined;
      state.customerCommentsPagination = undefined;
    },
    clearActiveLock: (state) => {
      state.activeLock = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.changeBalanceStatus.matchFulfilled,
      (state, action: any & PayloadAction<{ data: Balance }>) => {
        if (state.baseInformation && state.balances) {
          let nextBalances = ([] as Balance[]).concat(state.balances);

          if (action.meta.arg.originalArgs.body.active) {
            nextBalances.push(action.payload.data);
          } else if (
            nextBalances.find((item) => item.id === action.payload.data.id)
          ) {
            nextBalances = nextBalances.filter(
              (item) => item.id !== action.payload.data.id,
            );
          }

          state.balances = nextBalances;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.bindExternalOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: LinkedClient }>) => {
        if (state.baseInformation) {
          state.baseInformation = {
            ...state.baseInformation,
            pspIntegrationOrganization: action.payload.data,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getAllTopUpVisibility.matchFulfilled,
      (state, action: PayloadAction<Response<TopUpVisibility>>) => {
        if (state.baseInformation) {
          let nextTopUpVisibility: TopUpVisibility[] = [];

          if (state.topUpVisibility?.length) {
            nextTopUpVisibility = ([] as TopUpVisibility[]).concat(
              state.topUpVisibility,
            );
          }

          action?.payload?.data?.edges?.forEach((item) => {
            nextTopUpVisibility.push(item.node);
          });

          state.topUpVisibility = nextTopUpVisibility;
        }
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.getIndividualUser.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        state.customerId = action.payload.data.id;

        const fees: FeeItem[] = [];

        data.customFees?.forEach((item) => {
          fees.push({
            ...item,
            currency: '-',
            writeOffValue: '-',
            isModified: true,
          });
        });

        data.tariff?.fees.forEach((item) => {
          if (!fees.find((fee) => fee.feeId === item.feeId)) {
            fees.push({
              ...item,
              currency: '-',
              writeOffValue: '-',
            });
          }
        });

        // TODO: get riskScore
        let dataRiskProfile = { riskProfile: { totalRiskScore: 0 } };

        if (data.clientsScoreResult) {
          dataRiskProfile = {
            riskProfile: {
              totalRiskScore: data.clientsScoreResult.totalRiskScore,
            },
          };
        }

        let body = {
          id: data.id,
          firstName: data.firstName,
          applicationId: data?.application?.id || '',
          lastName: data.lastName,
          employmentStatus: data.employmentStatus,
          clientActivityId: data.clientActivityId,
          accounts: data.accounts.edges.map((item) => item.node),
          risk: dataRiskProfile.riskProfile.totalRiskScore,
          tariff: data.tariff ? data.tariff.name : 'No Tariff',
          tariffId: data?.tariff?.id,
          tariffRiskScoreId: data?.tariff?.riskScoreRangeId,
          idType: data?.idType,
          idNumber: data?.idNumber,
          idExpDate: data?.idExpDate,
          status: data.status,
          statusChangeReason: data?.statusChangeReason,
          clientScheme: data?.clientScheme,
          defaultFees: data?.tariff?.fees.map((item) => ({
            ...item,
            currency: '-',
            writeOffValue: '-',
          })),
          dateOfBirth: data?.user?.profile?.dob,
          phone: data?.user?.phone,
          email: data?.user?.profile?.additionalEmail || data?.user?.email,
          cards: ['MC debit *8478', 'MC corp *8788'],
          monthlyMaxValueIncomingPaymentRangeId:
            data.monthlyMaxValueIncomingPaymentRangeId,
          monthlyMaxValueOutgoingPaymentRangeId:
            data.monthlyMaxValueOutgoingPaymentRangeId,
          monthlyNumberIncomingTransactionsRangeId:
            data.monthlyNumberIncomingTransactionsRangeId,
          monthlyNumberOutgoingTransactionsRangeId:
            data.monthlyNumberOutgoingTransactionsRangeId,
          monthlyTurnoverRangeId: data.monthlyTurnoverRangeId,
          monthlyValueIncomingTransactionsRangeId:
            data.monthlyValueIncomingTransactionsRangeId,
          monthlyValueOutgoingTransactionsRangeId:
            data.monthlyValueOutgoingTransactionsRangeId,
          fees,
          type: data.type,
          userId: data?.userId,
          nationalityId: data?.nationalityId,
          taxNumber: data?.taxNumber,
          totpEnabled: data?.user?.totpEnabled,
          pspIntegrationOrganization: data?.pspIntegrationOrganization,
          birthCountryId: data?.birthCountryId,
          placeOfBirth: data?.placeOfBirth,
          nameOfEmployer: data?.nameOfEmployer,
          employeePosition: data?.employeePosition,
          countryOfEmployerId: data?.countryOfEmployerId,
          correspondenceAddress1: data?.correspondenceAddress1,
          correspondenceAddress2: data?.correspondenceAddress2,
          correspondenceAddressSameAsRegisteredAddress:
            data?.correspondenceAddressSameAsRegisteredAddress,
          correspondenceCity: data?.correspondenceCity,
          correspondenceCountryId: data?.correspondenceCountryId,
          correspondencePostalCode: data?.correspondencePostalCode,
          isUSResident: data?.isUSResident,
          typeOfActivityDescription: data?.typeOfActivityDescription,
          questionnaire: {
            ...data?.questionnaire,
          } as Questionnarie,
        };

        if (
          data?.clientScheme?.id === 'cf8b9900-6624-4854-b932-981c2af391b6' ||
          data?.clientScheme?.id === 'f8bac8c3-c8f0-4758-b886-4df566cac874'
        ) {
          let nextSourcesAndSizesOfWealth: {
            id?: string;
            sourceId: string;
            value?: string | number;
          }[] = [];
          let nextSourcesOfIncomes: {
            id?: string;
            sourceId: string;
            value?: string | number;
          }[] = [];

          if (data?.questionnaire?.sourcesAndSizesOfWealth) {
            if (
              typeof data?.questionnaire?.sourcesAndSizesOfWealth === 'string'
            ) {
              nextSourcesAndSizesOfWealth = JSON.parse(
                data?.questionnaire?.sourcesAndSizesOfWealth,
              ) as {
                sourceId: string;
                value?: string | number;
              }[];
            } else if (
              Array.isArray(data?.questionnaire?.sourcesAndSizesOfWealth)
            ) {
              nextSourcesAndSizesOfWealth =
                data?.questionnaire?.sourcesAndSizesOfWealth;
            }
          }

          if (data?.questionnaire?.sourcesOfIncomes) {
            if (typeof data?.questionnaire?.sourcesOfIncomes === 'string') {
              nextSourcesOfIncomes = JSON.parse(
                data?.questionnaire?.sourcesOfIncomes,
              ) as {
                sourceId: string;
                value?: string | number;
              }[];
            } else if (Array.isArray(data?.questionnaire?.sourcesOfIncomes)) {
              nextSourcesOfIncomes = data?.questionnaire?.sourcesOfIncomes;
            }
          }

          body = {
            ...body,
            questionnaire: {
              ...body?.questionnaire,
              sourcesAndSizesOfWealth: nextSourcesAndSizesOfWealth?.map(
                (sourceOfIncome) => ({
                  id: sourceOfIncome.sourceId,
                  ...sourceOfIncome,
                }),
              ),
              sourcesOfIncomes: nextSourcesOfIncomes?.map((sourceOfIncome) => ({
                id: sourceOfIncome.sourceId,
                ...sourceOfIncome,
              })),
            } as Questionnarie,
          };
        }

        state.baseInformation = {
          ...body,
        };

        state.baseInformationIsLoading = false;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.updateIndividualUserBaseInformation
        .matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        state.baseInformation = {
          ...state.baseInformation,
          firstName: data.firstName,
          lastName: data.lastName,
          dateOfBirth: data?.user?.profile?.dob,
          idType: data?.idType,
          idNumber: data?.idNumber,
          idExpDate: data?.idExpDate,
          nationalityId: data?.nationalityId,
          taxNumber: data?.taxNumber,
        };
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.getCustomerActiveLocks.matchFulfilled,
      (state, action: PayloadAction<Response<Lock>>) => {
        if (action?.payload?.data?.edges?.length) {
          state.activeLock = action?.payload?.data?.edges[0].node;
        } else {
          state.activeLock = undefined;
        }
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.blockIndividualCredit.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.creditBlock = data.creditBlock;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.unblockIndividualCredit.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.creditBlock = data.creditBlock;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.closeIndividualAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.closed = data.closed;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.updateIndividualVisibility
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: Account & { visibleToClient: boolean } }>,
      ) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.visibleToClient = data.visibleToClient;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.openIndividualAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.closed = data.closed;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.blockIndividualDebit.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.debitBlock = data.debitBlock;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.unblockIndividualDebit.matchFulfilled,
      (state, action: PayloadAction<{ data: Account }>) => {
        const { data } = action.payload;

        const nextBaseInformation = { ...state.baseInformation };
        const nextAccounts = nextBaseInformation.accounts?.map((item) => {
          const result = { ...item };

          if (result.id === data.id) {
            result.debitBlock = data.debitBlock;
          }

          return result;
        });

        nextBaseInformation.accounts = nextAccounts;

        state.baseInformation = nextBaseInformation;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomFee.matchFulfilled,
      (state, action: PayloadAction<{ data: FeeItem }>) => {
        const { data } = action.payload;
        if (state.baseInformation) {
          const nextFees = state.baseInformation?.fees?.map((item) => {
            let result = { ...item };

            if (result.feeId === data?.feeId) {
              result = {
                ...data,
                currency: 'GBP',
                writeOffValue: '-',
                isModified: true,
              };
            }

            return result;
          });

          state.baseInformation = {
            ...state.baseInformation,
            fees: nextFees,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomFee.matchFulfilled,
      (state, action: PayloadAction<{ data: FeeItem }>) => {
        if (state.baseInformation) {
          const defaultFee = state.baseInformation?.defaultFees?.find(
            (item) => item.feeId === action.payload.data.feeId,
          );

          const nextFees = state.baseInformation?.fees?.map((item) => {
            let result = { ...item };

            if (result.feeId === defaultFee?.feeId) {
              result = defaultFee;
            }

            return result;
          });

          state.baseInformation = {
            ...state.baseInformation,
            fees: nextFees,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.assignTariffToCustomer.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        const fees: FeeItem[] = [];

        data.customFees?.forEach((item) => {
          fees.push({
            ...item,
            currency: 'GBP',
            writeOffValue: '-',
            isModified: true,
          });
        });

        data.tariff?.fees.forEach((item) => {
          if (!fees.find((fee) => fee.feeId === item.feeId)) {
            fees.push({
              ...item,
              currency: 'GBP',
              writeOffValue: '-',
            });
          }
        });

        state.baseInformation = {
          ...state.baseInformation,
          tariff: data?.tariff ? data.tariff.name : 'No Tariff',
          tariffId: data?.tariff?.id,
          defaultFees: data?.tariff?.fees.map((item) => ({
            ...item,
            currency: 'GBP',
            writeOffValue: '-',
          })),
          fees,
        };
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.updateIndividualRanges.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        state.baseInformation = {
          ...state.baseInformation,
          monthlyTurnoverRangeId: data.monthlyTurnoverRangeId,
          monthlyNumberIncomingTransactionsRangeId:
            data.monthlyNumberIncomingTransactionsRangeId,
          monthlyNumberOutgoingTransactionsRangeId:
            data.monthlyNumberOutgoingTransactionsRangeId,
          monthlyValueIncomingTransactionsRangeId:
            data.monthlyValueIncomingTransactionsRangeId,
          monthlyValueOutgoingTransactionsRangeId:
            data.monthlyValueOutgoingTransactionsRangeId,
          monthlyMaxValueIncomingPaymentRangeId:
            data.monthlyMaxValueIncomingPaymentRangeId,
          monthlyMaxValueOutgoingPaymentRangeId:
            data.monthlyMaxValueOutgoingPaymentRangeId,
        };
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersTopUp.matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{
            id: string;
            companyName: string;
            visibleToClients: boolean;
          }>
        > & {
          meta: {
            arg: {
              originalArgs: { cursor?: string; companyName?: string };
            };
          };
        },
      ) => {
        if (state.customerId) {
          if (!state.topUpOptions) {
            state.topUpOptions = action.payload.data.edges.map(
              (item) => item.node,
            );
          } else if (
            state.topUpOptions &&
            action?.meta?.arg?.originalArgs?.cursor
          ) {
            const nextTopUpOptions = (
              [] as {
                id: string;
                companyName: string;
                visibleToClients: boolean;
              }[]
            ).concat(state.topUpOptions);

            action.payload.data.edges.forEach((item) => {
              if (
                !nextTopUpOptions.find((option) => option.id === item.node.id)
              ) {
                nextTopUpOptions.push(item.node);
              }
            });

            state.topUpOptions = nextTopUpOptions;
          }

          if (!action.meta.arg.originalArgs.companyName) {
            state.topUpOptionsPagination = action.payload.data.pageInfo;
          }

          state.topUpIsLoading = false;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersOverdraftStatus
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{
          data: Account & { overdraftEnabled?: boolean };
        }>,
      ) => {
        if (state.baseInformation) {
          const nextBaseInformation = { ...state.baseInformation };

          if (nextBaseInformation.accounts) {
            const nextAccounts = nextBaseInformation.accounts?.map((item) => {
              const result = { ...item };

              if (result.id === action.payload.data.id) {
                result.overdraftEnabled = action.payload.data.overdraftEnabled;
              }

              return result;
            }, []);

            nextBaseInformation.accounts = nextAccounts;

            state.baseInformation = nextBaseInformation;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersOverdraftBalanceValue
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Balance }>) => {
        state.balances = state.balances?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.closeOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        if (state.baseInformation) {
          const { data } = action.payload;

          // @ts-ignore
          state.baseInformation = {
            ...state.baseInformation,
            ...data,
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.reopenOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        if (state.baseInformation) {
          const { data } = action.payload;

          // @ts-ignore
          state.baseInformation = {
            ...state.baseInformation,
            ...data,
          };
        }
      },
    );
    builder
      .addMatcher(
        individualCustomersApi.endpoints.getMainChannel.matchFulfilled,
        (
          state,
          action: PayloadAction<
            Response<{
              id: string;
            }>
          >,
        ) => {
          if (
            action?.payload?.data?.edges &&
            action?.payload?.data?.edges.length &&
            action?.payload?.data?.edges[0]
          ) {
            state.mainChat = action?.payload?.data?.edges[0].node;
          }
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomerComments.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<DocumentComment>> & {
            meta: {
              arg: {
                originalArgs: {
                  cursor?: string;
                };
              };
            };
          },
        ) => {
          let comments;

          if (!action.meta?.arg?.originalArgs?.cursor) {
            comments = action.payload.data.edges?.map((item) => item.node);
          } else {
            comments = ([] as DocumentComment[]).concat(
              state.customerComments as DocumentComment[],
            );

            action.payload.data.edges?.forEach((item) =>
              comments.push(item.node),
            );
          }

          state.customerComments = comments;
          state.customerCommentsPagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.addCommentToCustomer.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          let comments: DocumentComment[] = [];

          if (state.customerComments) {
            comments = ([] as DocumentComment[]).concat(state.customerComments);
          }

          comments.unshift(action.payload.data);

          state.customerComments = comments;

          if (state.customerCommentsPagination) {
            state.customerCommentsPagination = {
              ...state.changeLogsPagination,
              totalCount:
                (state.customerCommentsPagination?.totalCount || 0) + 1,
            } as Pagination;
          }
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.editCommentInCustomer.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          state.customerComments = state.customerComments?.map((item) => {
            const result = { ...item };

            if (result.id === item.id) {
              result.text = action.payload.data.text;
            }

            return result;
          });
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.deleteCommentInCustomer.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          const nextTranscationComments = state?.customerComments?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          state.customerComments = nextTranscationComments;

          if (state.customerCommentsPagination) {
            state.customerCommentsPagination = {
              ...state.changeLogsPagination,
              totalCount:
                (state.customerCommentsPagination?.totalCount || 0) - 1,
            } as Pagination;
          }
        },
      );
  },
});

export const {
  setBalances,
  clearGeneralState,
  setConvertedToIndividual,
  clearActiveLock,
  setTopUpIds,
} = individualCustomerGeneralInfoSlice.actions;

export default individualCustomerGeneralInfoSlice.reducer;
